import React, { useCallback, useState, useRef, useEffect } from 'react';
import { theme, Button, Dropdown, Space, ColorPicker, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  RICHTEXT_EDITOR_OPTIONS,
  getRichTextEditorColorOptions
} from 'constants/richtextconstant';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  LinkOutlined,
  ClearOutlined,
  EditOutlined,
  EyeOutlined,
  FontColorsOutlined,
  BgColorsOutlined,
  StrikethroughOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  UndoOutlined,
  RedoOutlined,
  SaveOutlined,
  CopyOutlined
} from '@ant-design/icons';
import * as Styled from './RichTextEditor.styled';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSave?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  scheme?: string;
}

const { useToken } = theme;

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onSave,
  placeholder = 'Enter text...',
  disabled = false,
  scheme
}) => {
  const { token } = useToken();
  const { t } = useTranslation();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const isResizingRef = useRef(false);
  const historyRef = useRef<string[]>([value]);
  const historyIndexRef = useRef<number>(0);
  const lastSavedIndexRef = useRef<number>(0);

  useEffect(() => {
    setLocalValue(value);
    historyRef.current = [value];
    historyIndexRef.current = 0;
    lastSavedIndexRef.current = 0;
  }, [value, token, scheme]);

  const addToHistory = useCallback((newValue: string) => {
    historyRef.current = historyRef.current.slice(
      0,
      historyIndexRef.current + 1
    );
    historyRef.current.push(newValue);
    historyIndexRef.current = historyRef.current.length - 1;
    setLocalValue(newValue);
    setHasUnsavedChanges(historyIndexRef.current !== lastSavedIndexRef.current);
  }, []);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localValue);
    }
    lastSavedIndexRef.current = historyIndexRef.current;
    setHasUnsavedChanges(false);
  }, [localValue, onSave]);

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current--;
      const newValue = historyRef.current[historyIndexRef.current];
      setLocalValue(newValue);
      setHasUnsavedChanges(
        historyIndexRef.current !== lastSavedIndexRef.current
      );
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current++;
      const newValue = historyRef.current[historyIndexRef.current];
      setLocalValue(newValue);
      setHasUnsavedChanges(
        historyIndexRef.current !== lastSavedIndexRef.current
      );
    }
  }, []);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!containerRef.current) return;

      isResizingRef.current = true;
      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = containerRef.current.offsetWidth;
      const startHeight = containerRef.current.offsetHeight;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizingRef.current || !containerRef.current) return;
        const newWidth = Math.max(300, startWidth + moveEvent.clientX - startX);
        const newHeight = Math.max(
          200,
          startHeight + moveEvent.clientY - startY
        );
        containerRef.current.style.width = `${newWidth}px`;
        containerRef.current.style.height = `${newHeight}px`;
      };

      const handleMouseUp = () => {
        isResizingRef.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    []
  );

  const toggleFormatting = useCallback(
    (tag: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = localValue.substring(start, end);

      if (!selectedText) {
        return;
      }

      const openTag = `<${tag}>`;
      const closeTag = `</${tag}>`;

      const hasOpenTag = selectedText.includes(openTag);
      const hasCloseTag = selectedText.includes(closeTag);
      const isWrapped = hasOpenTag && hasCloseTag;

      let newValue: string;

      if (isWrapped) {
        const firstOpenIndex = selectedText.indexOf(openTag);
        const lastCloseIndex = selectedText.lastIndexOf(closeTag);

        const beforeTag = selectedText.substring(0, firstOpenIndex);
        const content = selectedText.substring(
          firstOpenIndex + openTag.length,
          lastCloseIndex
        );
        const afterTag = selectedText.substring(
          lastCloseIndex + closeTag.length
        );

        const unwrappedText = beforeTag + content + afterTag;
        newValue =
          localValue.substring(0, start) +
          unwrappedText +
          localValue.substring(end);

        addToHistory(newValue);

        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start, start + unwrappedText.length);
        }, 0);
      } else {
        const wrappedText = openTag + selectedText + closeTag;
        newValue =
          localValue.substring(0, start) +
          wrappedText +
          localValue.substring(end);

        addToHistory(newValue);

        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(
            start + openTag.length,
            start + openTag.length + selectedText.length
          );
        }, 0);
      }
    },
    [localValue, addToHistory]
  );

  const handleBold = useCallback(() => {
    toggleFormatting('b');
  }, [toggleFormatting]);

  const handleItalic = useCallback(() => {
    toggleFormatting('i');
  }, [toggleFormatting]);

  const handleUnderline = useCallback(() => {
    toggleFormatting('u');
  }, [toggleFormatting]);

  const handleStrikethrough = useCallback(() => {
    toggleFormatting('s');
  }, [toggleFormatting]);

  const handleHeading = useCallback(
    (level: string) => {
      const tag = `h${level}`;
      toggleFormatting(tag);
    },
    [toggleFormatting]
  );

  const handleFontSize = useCallback(
    (size: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = localValue.substring(start, end);

      if (!selectedText) return;

      const styleTag = `<span data-font-size="${size}">`;
      const closeTag = '</span>';
      const wrappedText = styleTag + selectedText + closeTag;
      const newValue =
        localValue.substring(0, start) +
        wrappedText +
        localValue.substring(end);

      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + wrappedText.length,
          start + wrappedText.length
        );
      }, 0);
    },
    [localValue, addToHistory]
  );

  const handleBulletList = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = localValue.substring(start, end);
    const bulletMarker = '\n• ';

    const lineStart = localValue.lastIndexOf('\n', start - 1) + 1;
    const lineText = localValue.substring(lineStart, end);
    const hasBullet = lineText.startsWith('• ');

    let newValue: string;

    if (hasBullet) {
      newValue =
        localValue.substring(0, lineStart) +
        lineText.substring(2) +
        localValue.substring(end);
      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start - 2, end - 2);
      }, 0);
    } else {
      newValue =
        localValue.substring(0, start) +
        bulletMarker +
        localValue.substring(start);
      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 3, end + 3);
      }, 0);
    }
  }, [localValue, addToHistory]);

  const handleOrderedList = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = localValue.substring(start, end);

    const lineStart = localValue.lastIndexOf('\n', start - 1) + 1;
    const lineText = localValue.substring(lineStart, end);
    const numberMatch = lineText.match(/^(\d+)\.\s/);
    const hasNumber = !!numberMatch;

    let newValue: string;

    if (hasNumber) {
      const numberLength = numberMatch![0].length;
      newValue =
        localValue.substring(0, lineStart) +
        lineText.substring(numberLength) +
        localValue.substring(end);
      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start - numberLength, end - numberLength);
      }, 0);
    } else {
      const numberMarker = '\n1. ';
      newValue =
        localValue.substring(0, start) +
        numberMarker +
        localValue.substring(start);
      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 4, end + 4);
      }, 0);
    }
  }, [localValue, addToHistory]);

  const handleLink = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = localValue.substring(start, end);

    if (!selectedText) {
      return;
    }

    const url = window.prompt(t('richTextEditor.enterUrl'));
    if (url) {
      const linkTag = `<a href="${url}" target="_blank">${selectedText}</a>`;
      const newValue =
        localValue.substring(0, start) + linkTag + localValue.substring(end);
      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + linkTag.length,
          start + linkTag.length
        );
      }, 0);
    }
  }, [localValue, addToHistory]);

  const handleCopyText = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = localValue.substring(start, end);

    if (selectedText) {
      navigator.clipboard.writeText(selectedText);
    }
  }, [localValue]);

  const applyFontColor = useCallback(
    (color: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = localValue.substring(start, end);

      if (!selectedText) {
        return;
      }

      const divPattern =
        /^<div class="text-(left|center|right)([^"]*)">([\s\S]+)<\/div>$/;
      const divMatch = selectedText.match(divPattern);

      let colorTag: string;

      if (color === 'none') {
        if (divMatch) {
          const extraClasses = divMatch[2].replace(/\s*font-color/g, '');
          colorTag = `<div class="text-${divMatch[1]}${extraClasses}">${divMatch[3]}</div>`;
        } else {
          colorTag = selectedText.replace(
            /<span class="font-color"[^>]*>([^<]*)<\/span>/g,
            '$1'
          );
        }
      } else {
        if (divMatch) {
          const extraClasses = divMatch[2].replace(/\s*font-color[^"]*/, '');
          colorTag = `<div class="text-${divMatch[1]}${extraClasses} font-color" data-color="${color}" style="--font-color: ${color}">${divMatch[3]}</div>`;
        } else {
          colorTag = `<span class="font-color" data-color="${color}" style="--font-color: ${color}">${selectedText}</span>`;
        }
      }

      const newValue =
        localValue.substring(0, start) + colorTag + localValue.substring(end);
      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + colorTag.length,
          start + colorTag.length
        );
      }, 0);
    },
    [localValue, addToHistory]
  );

  const applyBackgroundColor = useCallback(
    (color: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = localValue.substring(start, end);

      if (!selectedText) {
        return;
      }

      const divPattern =
        /^<div class="text-(left|center|right)([^"]*)">([\s\S]+)<\/div>$/;
      const divMatch = selectedText.match(divPattern);

      let bgTag: string;

      if (color === 'none') {
        if (divMatch) {
          const extraClasses = divMatch[2].replace(/\s*bg-color[^"]*/, '');
          bgTag = `<div class="text-${divMatch[1]}${extraClasses}">${divMatch[3]}</div>`;
        } else {
          bgTag = selectedText.replace(
            /<span class="bg-color"[^>]*>([^<]*)<\/span>/g,
            '$1'
          );
        }
      } else {
        if (divMatch) {
          const extraClasses = divMatch[2].replace(/\s*bg-color[^"]*/, '');
          bgTag = `<div class="text-${divMatch[1]}${extraClasses} bg-color" data-bg-color="${color}" style="--bg-color: ${color}">${divMatch[3]}</div>`;
        } else {
          bgTag = `<span class="bg-color" data-bg-color="${color}" style="--bg-color: ${color}">${selectedText}</span>`;
        }
      }

      const newValue =
        localValue.substring(0, start) + bgTag + localValue.substring(end);
      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + bgTag.length, start + bgTag.length);
      }, 0);
    },
    [localValue, addToHistory]
  );

  const handleAlignment = useCallback(
    (align: 'left' | 'center' | 'right') => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = localValue.substring(start, end);

      if (!selectedText) {
        return;
      }

      const alignPattern =
        /^<div class="text-(left|center|right)([^"]*)">([\s\S]+)<\/div>$/;
      const match = selectedText.match(alignPattern);

      let newValue: string;

      if (match && match[1] === align) {
        const unwrappedText = match[3];
        newValue =
          localValue.substring(0, start) +
          unwrappedText +
          localValue.substring(end);
      } else if (match) {
        const extraClasses = match[2];
        const alignTag = `<div class="text-${align}${extraClasses}">${match[3]}</div>`;
        newValue =
          localValue.substring(0, start) + alignTag + localValue.substring(end);
      } else {
        const alignTag = `<div class="text-${align}">${selectedText}</div>`;
        newValue =
          localValue.substring(0, start) + alignTag + localValue.substring(end);
      }

      addToHistory(newValue);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start,
          start + (newValue.length - localValue.length + (end - start))
        );
      }, 0);
    },
    [localValue, addToHistory]
  );

  const handleClear = useCallback(() => {
    addToHistory('');
  }, [addToHistory]);

  return (
    <Styled.EditorContainer ref={containerRef} token={token} scheme={scheme}>
      <Styled.ToolbarContainer token={token}>
        <Styled.SelectWrapper>
          <Select
            size='small'
            placeholder={t('richTextEditor.heading')}
            disabled={disabled || !isEditMode}
            onChange={handleHeading}
            options={RICHTEXT_EDITOR_OPTIONS.headings}
          />
        </Styled.SelectWrapper>
        <Styled.SelectWrapper>
          <Select
            size='small'
            placeholder={t('richTextEditor.fontSize')}
            disabled={disabled || !isEditMode}
            onChange={handleFontSize}
            options={RICHTEXT_EDITOR_OPTIONS.fontSizes}
          />
        </Styled.SelectWrapper>
        <Styled.ToolbarDivider token={token} />
        <Styled.ToolbarButton
          token={token}
          onClick={handleBold}
          title={t('richTextEditor.bold')}
          disabled={disabled || !isEditMode}
        >
          <BoldOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={handleItalic}
          title={t('richTextEditor.italic')}
          disabled={disabled || !isEditMode}
        >
          <ItalicOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={handleUnderline}
          title={t('richTextEditor.underline')}
          disabled={disabled || !isEditMode}
        >
          <UnderlineOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={handleStrikethrough}
          title={t('richTextEditor.strikethrough')}
          disabled={disabled || !isEditMode}
        >
          <StrikethroughOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarDivider token={token} />
        <Styled.ToolbarButton
          token={token}
          onClick={handleBulletList}
          title={t('richTextEditor.bulletList')}
          disabled={disabled || !isEditMode}
        >
          <UnorderedListOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={handleOrderedList}
          title={t('richTextEditor.orderedList')}
          disabled={disabled || !isEditMode}
        >
          <OrderedListOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarDivider token={token} />
        <Styled.ToolbarButton
          token={token}
          onClick={() => handleAlignment('left')}
          title={t('richTextEditor.alignLeft')}
          disabled={disabled || !isEditMode}
        >
          <AlignLeftOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={() => handleAlignment('center')}
          title={t('richTextEditor.alignCenter')}
          disabled={disabled || !isEditMode}
        >
          <AlignCenterOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={() => handleAlignment('right')}
          title={t('richTextEditor.alignRight')}
          disabled={disabled || !isEditMode}
        >
          <AlignRightOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarDivider token={token} />
        <Dropdown
          menu={{
            items: getRichTextEditorColorOptions(token).fontColors.map(
              (preset) => ({
                key: preset.value,
                label: (
                  <Styled.ColorPresetContainer>
                    {preset.value !== 'none' && (
                      <Styled.ColorBox color={preset.value} token={token} />
                    )}
                    {preset.label}
                  </Styled.ColorPresetContainer>
                ),
                onClick: () => {
                  applyFontColor(preset.value);
                }
              })
            )
          }}
          trigger={['click']}
          onOpenChange={(open) => {
            if (!open) {
              textareaRef.current?.focus();
            }
          }}
        >
          <Styled.ToolbarButton
            token={token}
            title={t('richTextEditor.fontColor')}
            disabled={disabled || !isEditMode}
          >
            <FontColorsOutlined />
          </Styled.ToolbarButton>
        </Dropdown>
        <Dropdown
          menu={{
            items: getRichTextEditorColorOptions(token).bgColors.map(
              (preset) => ({
                key: preset.value,
                label: (
                  <Styled.ColorPresetContainer>
                    {preset.value !== 'none' && (
                      <Styled.ColorBox color={preset.value} token={token} />
                    )}
                    {preset.label}
                  </Styled.ColorPresetContainer>
                ),
                onClick: () => {
                  applyBackgroundColor(preset.value);
                }
              })
            )
          }}
          trigger={['click']}
          onOpenChange={(open) => {
            if (!open) {
              textareaRef.current?.focus();
            }
          }}
        >
          <Styled.ToolbarButton
            token={token}
            title={t('richTextEditor.backgroundColor')}
            disabled={disabled || !isEditMode}
          >
            <BgColorsOutlined />
          </Styled.ToolbarButton>
        </Dropdown>
        <Styled.ToolbarDivider token={token} />
        <Styled.ToolbarButton
          token={token}
          onClick={handleLink}
          title={t('richTextEditor.addLink')}
          disabled={disabled || !isEditMode}
        >
          <LinkOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={handleCopyText}
          title={t('richTextEditor.copyText')}
          disabled={disabled || !isEditMode}
        >
          <CopyOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={handleClear}
          title={t('richTextEditor.clearAll')}
          disabled={disabled || !isEditMode}
        >
          <ClearOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarDivider token={token} />
        <Styled.ToolbarButton
          token={token}
          onClick={handleUndo}
          title={t('richTextEditor.undo')}
          disabled={disabled || !isEditMode || historyIndexRef.current === 0}
        >
          <UndoOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarButton
          token={token}
          onClick={handleRedo}
          title={t('richTextEditor.redo')}
          disabled={
            disabled ||
            !isEditMode ||
            historyIndexRef.current === historyRef.current.length - 1
          }
        >
          <RedoOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarDivider token={token} />
        <Styled.ToolbarButton
          token={token}
          onClick={handleSave}
          title={t('richTextEditor.save')}
          disabled={disabled || !isEditMode || !hasUnsavedChanges}
        >
          <SaveOutlined />
        </Styled.ToolbarButton>
        <Styled.ToolbarDivider token={token} />
        <Styled.ModeToggleButton
          token={token}
          type={isEditMode ? 'primary' : 'default'}
          size='small'
          icon={isEditMode ? <EyeOutlined /> : <EditOutlined />}
          onClick={() => {
            setIsEditMode(!isEditMode);
          }}
          disabled={disabled}
        >
          {isEditMode ? t('richTextEditor.preview') : t('richTextEditor.edit')}
        </Styled.ModeToggleButton>
      </Styled.ToolbarContainer>
      <Styled.EditorContentWrapper token={token}>
        {isEditMode ? (
          <Styled.StyledTextarea
            token={token}
            scheme={scheme}
            ref={textareaRef}
            value={localValue}
            onChange={(e) => {
              const newValue = e.target.value;
              addToHistory(newValue);
            }}
            placeholder={placeholder}
            disabled={disabled}
            rows={6}
          />
        ) : (
          <Styled.PreviewContainer
            token={token}
            scheme={scheme}
            dangerouslySetInnerHTML={{
              __html:
                localValue ||
                `<p class="no-content">${t('richTextEditor.noContent')}</p>`
            }}
          />
        )}
      </Styled.EditorContentWrapper>
      <Styled.ResizeHandle
        token={token}
        onMouseDown={handleResizeStart}
        title={t('richTextEditor.dragToResize')}
      />
    </Styled.EditorContainer>
  );
};

export default RichTextEditor;
